import {
  Component,
  EventEmitter,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DOCUMENT } from '@angular/common';

import { catchError, finalize, map, of } from 'rxjs';
import moment from 'moment';

import { COUNTRIES } from '@shared/core/constants';
import { Location } from '@shared/location/models';
import { ImportedReservation } from '@shared/party/models';
import { TelService } from '@tablesready/common-ui';
import { getEnabledCustomFieldNames } from '@shared/party';
import { ImportService } from '../../services';
import { AlertsService } from '@tablesready/frontend-alerts-bar';
import { ApiClientService } from '@tablesready/api-client';

@Component({
  selector: 'tablesready-import-form',
  styleUrls: ['./import-form.components.scss'],
  templateUrl: './import-form.component.html',
})
export class ImportFormComponent implements OnChanges, OnDestroy {
  @Input() location: Location;
  @Output() onImportedData = new EventEmitter<ImportedReservation[]>();

  form: UntypedFormGroup;
  submitting = false;
  error = '';
  countries = COUNTRIES;
  country: any;
  timezones: string[];
  file: File;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly alertService: AlertsService,
    private readonly importService: ImportService,
    private readonly telService: TelService,
    private readonly http: ApiClientService,
    @Inject(DOCUMENT) private document: HTMLDocument,
  ) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.location.currentValue) {
      this.country = COUNTRIES.find(country => country.abbr === this.location.country);
      this.timezones = [...this.country.zones];
      this.initForm();
    }
  }

  ngOnDestroy(): void {
    this.onImportedData.complete();
  }

  submit() {
    if (this.submitting) {
      return;
    }

    const {file, ...rest} = this.form.value;
    this.importService.uploadFile(rest, this.file)
      .pipe(
        map(({parties}) => {
          this.alertService.addSuccess(
            `${parties.length} ${this.location.displayOptions.labels.reservation_plural}, verify and save changes to proceed.`,
            'Uploaded:'
          );
          this.onImportedData.emit(
            parties.map((party) => {
            party.phone = this.telService.format(party.phone, this.country?.abbr);
            return party;
          }))
        }),
        catchError(error => {
          this.error = error;

          return of();
        }),
        finalize(() => this.submitting = false)
      )
      .subscribe();
  }

  onAddFile(files: FileList) {
    this.file = files[0];
  }

  downloadFile(): void{
    this.http.get('/v.1/reservations/sample',{/*headers,*/ responseType: 'blob' as 'json'}).subscribe(
      (response: any) =>{
        let dataType = response.type;
        let binaryData = [];
        binaryData.push(response);
        let downloadLink = this.document.createElement('a');
        downloadLink.href = window.URL.createObjectURL(new Blob(binaryData, {type: dataType}));
        downloadLink.setAttribute('download', 'SampleCSV.csv');
        this.document.body.appendChild(downloadLink);
        downloadLink.click();
      }
    )
  }

  getFormatText() {
    return [
      `Date`,
      'Time',
      'Name',
      'Size',
      'Phone',
      'Notes',
      ...getEnabledCustomFieldNames(this.location.partyFields),
    ].join(', ');
  }

  getTomorrowDate() {
    return moment().add(1, 'day').format('M/D/YYYY');
  }

  private initForm() {
    this.form = this.formBuilder.group({
      file: [null, [Validators.required]],
      timezone: [this.location.timezone || this.timezones[0]],
      delimiter: [',', [Validators.required, Validators.maxLength(1)]],
      header: [true, [Validators.required]],
    });
  }
}
